import { useEffect, useMemo, useState } from "react";
import { Receipt, Wallet, AlertTriangle, TrendingUp } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { useTenant } from "@/contexts/TenantContext";
import { api, getActiveBranchName } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

import { DateRangeFilter, presetRange } from "@/components/reports/DateRangeFilter";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { KpiCard, formatMxn, formatInt } from "@/components/reports/KpiCard";
import { ShareExportPanel } from "@/components/reports/ShareExportPanel";

export default function SalesReport() {
  const { isSuperAdmin, activeTenantId } = useTenant();
  const [range, setRange] = useState(() => ({ preset: "month", ...presetRange("month") }));
  const [branchId, setBranchId] = useState("");
  const [groupBy, setGroupBy] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { group_by: groupBy };
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    if (branchId) p.branch_id = branchId;
    if (isSuperAdmin && activeTenantId) p.tenant_id = activeTenantId;
    return p;
  }, [range, branchId, groupBy, isSuperAdmin, activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/reports/sales", { params });
        if (!cancelled) setData(d);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  const t = data?.totals || {};
  const groups = data?.groups || [];
  const detail = data?.detail || [];

  return (
    <div className="space-y-6" data-testid="page-sales-report">
      <PageHeader
        title="Reporte de ventas"
        description="Ventas cerradas por período, con detalle por folio y agrupación."
        actions={
          <ShareExportPanel
            reportKey="sales"
            reportName="Reporte de ventas"
            endpoint="/reports/sales"
            params={params}
            exportPermission="reports:export"
            filename="ventas"
            branchId={branchId || null}
            filters={{
              date_from: range.date_from,
              date_to: range.date_to,
              branch_name: getActiveBranchName(),
              group_by: groupBy,
            }}
            stats={{
              sales_total: data?.totals?.sales_total,
              sales_count: data?.totals?.sales_count,
              avg_ticket: data?.totals?.avg_ticket,
              payments_total: data?.totals?.payments_total,
              balance_pending: data?.totals?.balance_pending,
              row_count: detail.length,
            }}
            testIdPrefix="sales-share"
          />
        }
      />

      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <DateRangeFilter value={range} onChange={setRange} />
          <div className="flex flex-wrap items-end gap-3">
            <BranchFilter value={branchId} onChange={setBranchId} />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Agrupar
              </div>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="h-9 w-36" data-testid="sales-groupby">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Por día</SelectItem>
                  <SelectItem value="branch">Por sucursal</SelectItem>
                  <SelectItem value="none">Sin agrupar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sales-kpis">
        <KpiCard label="Total vendido" value={formatMxn(t.sales_total)} icon={Receipt} loading={loading} testId="sales-total" />
        <KpiCard label="# Ventas" value={formatInt(t.sales_count)} icon={TrendingUp} loading={loading} testId="sales-count" />
        <KpiCard label="Ticket promedio" value={formatMxn(t.avg_ticket)} icon={Wallet} loading={loading} testId="sales-avg" />
        <KpiCard label="Anticipos" value={formatMxn(t.payments_total)} icon={Wallet} loading={loading} testId="sales-payments" />
      </div>

      {/* Groups */}
      {groups && groups.length > 0 && (
        <Card className="border-border" data-testid="sales-groups">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Agrupación · {groupBy === "day" ? "Por día" : "Por sucursal"}</CardTitle>
            <CardDescription>Total y conteo por grupo.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-72 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{groupBy === "day" ? "Fecha" : "Sucursal"}</TableHead>
                    <TableHead className="text-right">Ventas</TableHead>
                    <TableHead className="text-right"># Órdenes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((g) => (
                    <TableRow key={g.key}>
                      <TableCell className="font-mono text-xs">{g.label}</TableCell>
                      <TableCell className="text-right">{formatMxn(g.total)}</TableCell>
                      <TableCell className="text-right">{g.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail */}
      <Card className="border-border" data-testid="sales-detail">
        <CardHeader className="border-b border-border">
          <CardTitle className="tracking-tight">Detalle de ventas</CardTitle>
          <CardDescription>{detail.length} ventas cerradas en el período.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}
            </div>
          ) : detail.length ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Folio</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Vehículo</TableHead>
                    <TableHead>Sucursal</TableHead>
                    <TableHead>Estatus</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Pagado</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.map((r) => (
                    <TableRow key={r.sale_id} data-testid={`sale-row-${r.sale_id}`}>
                      <TableCell className="font-mono text-xs">{r.folio || "—"}</TableCell>
                      <TableCell className="text-xs">{r.closed_date}</TableCell>
                      <TableCell className="text-sm">{r.customer_name}</TableCell>
                      <TableCell className="text-sm">{r.vehicle_label}</TableCell>
                      <TableCell className="text-sm">{r.branch_name || "—"}</TableCell>
                      <TableCell><Badge variant="secondary" className="capitalize text-[10px]">{r.so_status}</Badge></TableCell>
                      <TableCell className="text-right">{formatMxn(r.total)}</TableCell>
                      <TableCell className="text-right">{formatMxn(r.paid)}</TableCell>
                      <TableCell className="text-right">
                        {r.balance > 0.005 ? (
                          <span className="text-amber-600 font-semibold">{formatMxn(r.balance)}</span>
                        ) : (
                          formatMxn(0)
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-6 text-sm text-muted-foreground">Sin ventas cerradas en el período.</div>
          )}
        </CardContent>
      </Card>

      {t.balance_pending > 0 && (
        <div className="text-xs text-amber-700 flex items-center gap-2">
          <AlertTriangle className="h-3 w-3" />
          Saldo pendiente acumulado en el período: <b>{formatMxn(t.balance_pending)}</b>
        </div>
      )}
    </div>
  );
}
